import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getVisitorProperty,
  getVisitorUnits,
  VisitorProperty,
  VisitorUnit,
} from '../../api/visitor';
import { Room, RoomEvent, Track, RemoteTrack, RemoteTrackPublication, RemoteParticipant } from 'livekit-client';
import { apiOrigin, IS_VISITOR_SURFACE } from '../../utils/surface';

const LIVEKIT_URL = 'wss://intrlock-j4f640sq.livekit.cloud';
const VIDEO_API = `${apiOrigin()}/video`;

/**
 * Tries multiple times to capture and upload a visitor snapshot.
 * Tries the LiveKit track's MediaStreamTrack first (most reliable on
 * mobile), falls back to the rendered <video> element. Retries every
 * 1.5s up to 5 attempts; stops on first success.
 */
async function captureSnapshotWithRetries(
  videoTrack: any,
  videoEl: HTMLVideoElement | null,
  roomId: string,
  attempts = 10,
) {
  console.log('[snapshot] starting capture loop', { hasTrack: !!videoTrack, hasVideoEl: !!videoEl });
  for (let i = 0; i < attempts; i++) {
    const elState = videoEl
      ? { vw: videoEl.videoWidth, vh: videoEl.videoHeight, ready: videoEl.readyState }
      : null;
    const mst = videoTrack?.mediaStreamTrack as MediaStreamTrack | undefined;
    const trackState = mst ? { kind: mst.kind, ready: mst.readyState, enabled: mst.enabled } : null;
    console.log(`[snapshot] attempt ${i + 1}/${attempts}`, { elState, trackState });

    // Strategy 1: rendered <video> element first (works on iOS w/o
    // playback restrictions because the element is already on-screen).
    try {
      if (videoEl && videoEl.videoWidth > 0 && videoEl.readyState >= 2) {
        const dataUrl = await captureFromVideoEl(videoEl);
        if (dataUrl && dataUrl.length > 1000) {
          console.log('[snapshot] captured via visible <video>, size:', dataUrl.length);
          await postSnapshot(dataUrl, roomId);
          return;
        }
      }
    } catch (err) {
      console.error('[snapshot] videoEl capture failed:', err);
    }

    // Strategy 2: LiveKit MediaStreamTrack via ImageCapture or off-DOM video
    try {
      if (mst && mst.readyState === 'live') {
        const dataUrl = await captureFromTrack(mst);
        if (dataUrl && dataUrl.length > 1000) {
          console.log('[snapshot] captured via track, size:', dataUrl.length);
          await postSnapshot(dataUrl, roomId);
          return;
        }
      }
    } catch (err) {
      console.error('[snapshot] track capture failed:', err);
    }

    await new Promise(r => setTimeout(r, 1200));
  }
  console.warn('[snapshot] gave up after', attempts, 'attempts');
}

async function captureFromTrack(track: MediaStreamTrack): Promise<string | null> {
  // Use ImageCapture if available (desktop Chrome/Android); iOS
  // Safari/Chrome don't support it.
  const ImageCaptureCtor = (window as any).ImageCapture;
  if (ImageCaptureCtor) {
    try {
      const ic = new ImageCaptureCtor(track);
      const blob: Blob = await ic.takePhoto({ imageWidth: 480 });
      return await blobToJpegDataUrl(blob);
    } catch {
      // fall through to MediaStream approach
    }
  }
  // Fallback: pipe the track through a hidden DOM <video>. iOS
  // Safari/Chrome will NOT autoplay videos that aren't attached to
  // the DOM, so we briefly append the element off-screen, wait for
  // a frame, capture, then remove.
  const stream = new MediaStream([track]);
  const v = document.createElement('video');
  v.srcObject = stream;
  v.muted = true;
  v.playsInline = true;
  v.setAttribute('muted', '');
  v.setAttribute('playsinline', '');
  v.setAttribute('autoplay', '');
  v.style.position = 'fixed';
  v.style.left = '-9999px';
  v.style.top = '0';
  v.style.width = '1px';
  v.style.height = '1px';
  v.style.opacity = '0';
  document.body.appendChild(v);
  try {
    await v.play().catch(() => {});
    // Wait until the element exposes a real frame (videoWidth > 0).
    const start = Date.now();
    while (Date.now() - start < 4000) {
      if (v.videoWidth > 0 && v.videoHeight > 0 && v.readyState >= 2) break;
      await new Promise(r => setTimeout(r, 100));
    }
    if (!v.videoWidth) return null;
    // Extra small delay so the first decoded frame is fully painted.
    await new Promise(r => setTimeout(r, 150));
    return captureFromVideoEl(v);
  } finally {
    try { v.pause(); v.srcObject = null; } catch {}
    v.remove();
  }
}

async function captureFromVideoEl(video: HTMLVideoElement): Promise<string | null> {
  if (!video.videoWidth || !video.videoHeight) return null;
  const targetW = 480;
  const scale = Math.min(1, targetW / video.videoWidth);
  const w = Math.round(video.videoWidth * scale);
  const h = Math.round(video.videoHeight * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.7);
}

async function blobToJpegDataUrl(blob: Blob): Promise<string> {
  // ImageCapture may give PNG — re-encode to JPEG via canvas to keep size small.
  const bitmap = await createImageBitmap(blob);
  const targetW = 480;
  const scale = Math.min(1, targetW / bitmap.width);
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(bitmap, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.7);
}

async function postSnapshot(dataUrl: string, roomId: string) {
  const res = await fetch(`${VIDEO_API}/call-snapshot`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ roomId, imageBase64: dataUrl }),
  });
  console.log('[snapshot] upload response status:', res.status);
}

/**
 * Resolves once the <video> element has a renderable frame
 * (videoWidth/Height > 0 and at least HAVE_CURRENT_DATA). Polls every
 * 100ms with a 6-second cap so we don't hang forever if the camera
 * never starts. Resolves to null on timeout.
 */
function whenVideoReady(video: HTMLVideoElement, timeoutMs = 6000): Promise<HTMLVideoElement | null> {
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const ready = video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2;
      if (ready) {
        // Give the encoder one extra frame so we don't capture a black
        // image right at the first signal.
        setTimeout(() => resolve(video), 250);
        return;
      }
      if (Date.now() - start > timeoutMs) { resolve(null); return; }
      setTimeout(tick, 100);
    };
    tick();
  });
}

/**
 * Captures a frame from the visitor's local <video> element and uploads
 * it as a JPEG data URL to /video/call-snapshot. The backend stores it
 * on the call_events row so it appears in the call history (whether the
 * tenant answered or not).
 */
async function captureAndUploadSnapshot(video: HTMLVideoElement, roomId: string) {
  try {
    if (!video.videoWidth || !video.videoHeight) return;
    // Downscale to ~480px wide for a small, fast-uploading snapshot.
    const targetW = 480;
    const scale = Math.min(1, targetW / video.videoWidth);
    const w = Math.round(video.videoWidth * scale);
    const h = Math.round(video.videoHeight * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    // 0.7 quality keeps under ~50KB for typical webcam frames.
    const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
    await fetch(`${VIDEO_API}/call-snapshot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roomId, imageBase64: dataUrl }),
    });
  } catch (err) {
    console.error('[snapshot] capture/upload failed:', err);
  }
}

type CallStatus = 'loading' | 'name-entry' | 'calling' | 'connected' | 'ended' | 'error' | 'no-answer';

export const VisitorCall: React.FC = () => {
  const { propertyId, unitNumber } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [property, setProperty] = useState<VisitorProperty | null>(null);
  const [unit, setUnit] = useState<VisitorUnit | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  const [visitorName, setVisitorName] = useState(searchParams.get('name') || '');
  const [status, setStatus] = useState<CallStatus>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [audioDiagnostics, setAudioDiagnostics] = useState<string[]>([]);

  const roomRef = useRef<Room | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const durationRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const logAudio = useCallback((event: string, details: Record<string, unknown> = {}) => {
    const entry = `${new Date().toLocaleTimeString()} ${event} ${JSON.stringify(details)}`;
    console.info('[visitor-audio]', event, details);
    setAudioDiagnostics((current) => [...current.slice(-19), entry]);
  }, []);

  // Load property & unit
  useEffect(() => {
    (async () => {
      try {
        let prop: VisitorProperty;
        if (propertyId) prop = await getVisitorProperty(propertyId);
        else { setLoadError('No property specified.'); return; }
        setProperty(prop);

        const units = await getVisitorUnits(prop.id);
        const target = units.find((u: VisitorUnit) => u.unit_number === unitNumber);
        if (!target) { setLoadError(`Unit ${unitNumber} not found.`); return; }
        setUnit(target);
        setStatus('name-entry');
      } catch (err: any) {
        setLoadError(err.message || 'Failed to load');
      }
    })();
  }, [propertyId, unitNumber]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (roomRef.current) roomRef.current.disconnect();
      if (durationRef.current) clearInterval(durationRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleCall = useCallback(async () => {
    if (!unit || !property || !visitorName.trim()) return;
    
    setStatus('calling');
    setErrorMsg(null);
    setAudioDiagnostics([]);
    logAudio('call-start', {
      secureContext: window.isSecureContext,
      userAgent: navigator.userAgent,
      visibility: document.visibilityState,
    });

    // 60-second timeout
    timeoutRef.current = setTimeout(() => {
      setStatus('no-answer');
      if (roomRef.current) roomRef.current.disconnect();
    }, 60000);

    try {
      // The visitor directory passes ?tenant=<person_id> when one is
      // selected. Forward it to /video/call so the lambda pushes to that
      // specific tenant instead of falling back to the unit's primary.
      const tenantId = searchParams.get('tenant') || undefined;
      const res = await fetch(`${VIDEO_API}/call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          unitId: unit.id,
          unitNumber: unit.unit_number,
          propertyId: property.id,
          visitorName: visitorName.trim(),
          ...(tenantId ? { tenantId } : {}),
        }),
      });
      const data = await res.json();

      if (!res.ok || !data.token) {
        // Surface specific errors from the Lambda as user-friendly messages
        const errMsg = data.error || 'Failed to start call';
        if (errMsg.includes('no video app') || errMsg.includes('not activated')) {
          throw new Error('Tenant has no video app activated. They need to set up the Intrlock app first.');
        }
        if (errMsg.includes('Could not find tenant') || errMsg.includes('unit')) {
          throw new Error('Could not find unit. Please check the unit number and try again.');
        }
        throw new Error(errMsg);
      }

      // Connect to LiveKit with explicit options
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        publishDefaults: {
          videoCodec: 'vp8',
        },
      });
      roomRef.current = room;

      // Handle remote tracks
      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, pub: RemoteTrackPublication, participant: RemoteParticipant) => {
        if (track.kind === Track.Kind.Video && remoteVideoRef.current) {
          track.attach(remoteVideoRef.current);
          // LiveKit sets object-fit:cover inline which crops the video — override to contain
          remoteVideoRef.current.style.objectFit = 'contain';
        }
        if (track.kind === Track.Kind.Audio) {
          const audioEl = track.attach();
          audioEl.autoplay = true;
          document.body.appendChild(audioEl);
          const mediaTrack = (track as any).mediaStreamTrack as MediaStreamTrack | undefined;
          logAudio('remote-audio-subscribed', {
            participant: participant.identity,
            publicationMuted: pub.isMuted,
            trackEnabled: mediaTrack?.enabled,
            trackMuted: mediaTrack?.muted,
            readyState: mediaTrack?.readyState,
            settings: mediaTrack?.getSettings?.(),
          });
          void audioEl.play()
            .then(() => logAudio('remote-audio-playing', { paused: audioEl.paused, volume: audioEl.volume }))
            .catch((error) => logAudio('remote-audio-play-failed', { error: String(error) }));
        }
        // Tenant joined - clear timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setStatus('connected');
        // Start duration timer
        durationRef.current = setInterval(() => setDuration(d => d + 1), 1000);
      });

      // Detect when tenant joins the room (before they publish tracks)
      room.on(RoomEvent.ParticipantConnected, () => {
        console.log('Participant connected — tenant answered');
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        setStatus('connected');
        if (!durationRef.current) {
          durationRef.current = setInterval(() => setDuration(d => d + 1), 1000);
        }
      });

      room.on(RoomEvent.Disconnected, (reason) => {
        logAudio('room-disconnected', { reason: String(reason ?? '') });
        setStatus('ended');
        if (durationRef.current) clearInterval(durationRef.current);
      });

      room.on(RoomEvent.ParticipantDisconnected, () => {
        room.disconnect();
      });

      // Mobile Safari is the visitor in most cases. The old flow did
      // `getUserMedia` for permission, immediately stopped those tracks,
      // then asked LiveKit to re-acquire on connect. On iOS Safari that
      // sequence intermittently leaves the mic unpublished — the tenant
      // sees the visitor's video but can't hear them. Let LiveKit own
      // the gUM lifecycle: connect first, then enable camera + mic
      // explicitly and individually so a partial failure (just mic) is
      // visible in the console and can be retried.
      // LiveKit JWT prefix removed from logs — it's not a full token,
      // but visitor-side credentials shouldn't be printed in any form.
      try {
      await room.connect(LIVEKIT_URL, data.token);
      logAudio('room-connected', { remoteParticipants: room.remoteParticipants.size });
      try {
        await room.startAudio();
        logAudio('audio-context-started');
      } catch (audioStartError) {
        logAudio('audio-context-start-failed', { error: String(audioStartError) });
      }
      } catch (connectErr: any) {
        throw new Error(`Connection failed: ${connectErr?.message ?? connectErr}`);
      }
      console.log('Connected to LiveKit!');

      // Enable camera + mic separately so we can tell which one fails
      // on mobile Safari (the typical culprit). Retry mic once if its
      // publication doesn't appear within a beat — Safari sometimes
      // returns the track before publishing, and the resulting silent
      // call exactly matches what Aaron is reporting.
      try {
        await room.localParticipant.setCameraEnabled(true);
      } catch (camErr: any) {
        console.warn('[call] camera enable failed:', camErr?.message ?? camErr);
      }
      const ensureMic = async (attempt: number): Promise<void> => {
        await room.localParticipant.setMicrophoneEnabled(true);
        // Give Safari a moment to publish before checking.
        await new Promise((r) => setTimeout(r, 250));
        const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone);
        const published = pub && !pub.isMuted && pub.track;
        if (!published && attempt < 2) {
          console.warn('[call] mic not published, retrying…', { attempt });
          await room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
          await new Promise((r) => setTimeout(r, 200));
          return ensureMic(attempt + 1);
        }
        if (!published) {
          console.error('[call] mic still not published after retries — visitor will be inaudible');
          logAudio('local-mic-publish-failed', { attempt });
        } else {
          console.log('[call] mic published');
          const mediaTrack = (pub.track as any)?.mediaStreamTrack as MediaStreamTrack | undefined;
          let permission = 'unknown';
          try { permission = (await navigator.permissions.query({ name: 'microphone' as PermissionName })).state; } catch {}
          logAudio('local-mic-published', {
            permission,
            publicationMuted: pub.isMuted,
            trackEnabled: mediaTrack?.enabled,
            trackMuted: mediaTrack?.muted,
            readyState: mediaTrack?.readyState,
            settings: mediaTrack?.getSettings?.(),
          });
        }
      };
      // Don't throw if mic enable fails — let the call proceed (camera-only)
      // so the visitor isn't dumped to an error screen on a transient
      // permission hiccup. The mic-publish warning is logged instead.
      try {
        await ensureMic(0);
      } catch (micErr: any) {
        console.error('[call] mic enable failed (continuing without audio):', micErr?.message ?? micErr);
      }

      // Attach local video
      const localTrack = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
      if (localTrack && localVideoRef.current) {
        localTrack.attach(localVideoRef.current);
        // Preserve the full camera frame on desktop and mobile. Cropping this
        // preview made it look as though the web app was hiding the background.
        localVideoRef.current.style.objectFit = 'contain';
      }

      // Capture a snapshot of the visitor and attach it to the call
      // record. Runs whether or not the tenant answers. We try two
      // capture sources: LiveKit's MediaStreamTrack (most reliable on
      // mobile) and the local <video> element as fallback.
      const roomNameForSnapshot = data.roomId || data.room_name || data.room;
      console.log('[snapshot] roomId for snapshot:', roomNameForSnapshot);
      if (roomNameForSnapshot) {
        const videoTrack = room.localParticipant
          .getTrackPublication(Track.Source.Camera)?.track;
        captureSnapshotWithRetries(
          videoTrack as any,
          localVideoRef.current,
          roomNameForSnapshot,
        );
      }

    } catch (err: any) {
      console.error('Call error:', err);
      const msg = err.message || 'Call failed';
      // Add more context for common errors
      if (msg.includes('signal') || msg.includes('fetch')) {
        setErrorMsg(`Connection failed: ${msg}. Check camera/mic permissions.`);
      } else {
        setErrorMsg(msg);
      }
      setStatus('error');
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  }, [unit, property, visitorName, searchParams, logAudio]);

  const handleHangup = useCallback(() => {
    if (roomRef.current) roomRef.current.disconnect();
    setStatus('ended');
    if (durationRef.current) clearInterval(durationRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  const toggleMute = useCallback(() => {
    if (roomRef.current) {
      roomRef.current.localParticipant.setMicrophoneEnabled(muted);
      setMuted(!muted);
    }
  }, [muted]);

  const toggleVideo = useCallback(() => {
    if (roomRef.current) {
      roomRef.current.localParticipant.setCameraEnabled(!videoEnabled);
      setVideoEnabled(!videoEnabled);
    }
  }, [videoEnabled]);

  const formatTime = (s: number) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const visitorBase = IS_VISITOR_SURFACE ? '' : '/visit';
  const goBack = useCallback(() => {
    navigate(`${visitorBase}/${propertyId}`);
  }, [navigate, visitorBase, propertyId]);
  const handleTryAgain = () => window.location.reload();

  // Auto-return to the directory when the call terminates so the room
  // doesn't linger and visitors don't sit on a stale "ended" screen.
  // Triggered for both tenant-ended (status='ended') and tenant-didn't-
  // answer (status='no-answer') paths. Brief 3s delay so the visitor
  // can see the closing message before being navigated away.
  useEffect(() => {
    if (status === 'ended' || status === 'no-answer') {
      const t = setTimeout(() => goBack(), 3000);
      return () => clearTimeout(t);
    }
  }, [status, goBack]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
      </div>
    );
  }

  // Load error
  if (loadError) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">{loadError}</p>
          <button onClick={goBack} className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg">Go Back</button>
        </div>
      </div>
    );
  }

  // Name entry
  if (status === 'name-entry') {
    return (
      <div className="min-h-screen hero-gradient grid-pattern flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <h1 className="text-white text-2xl font-semibold mb-2">Video Call</h1>
            <p className="text-gray-400">Unit {unit?.unit_number} • {property?.name}</p>
          </div>
          <div className="bg-gray-800 rounded-2xl p-6">
            <label className="block text-gray-300 text-sm mb-2">Your Name</label>
            <input
              type="text"
              value={visitorName}
              onChange={(e) => setVisitorName(e.target.value)}
              placeholder="Enter your name"
              className="w-full bg-gray-700 text-white rounded-lg px-4 py-3 mb-4 outline-none focus:ring-2 focus:ring-emerald-500"
              autoFocus
            />
            <button
              onClick={handleCall}
              disabled={!visitorName.trim()}
              className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-3 rounded-lg transition-colors"
            >
              Start Call
            </button>
          </div>
          <button onClick={goBack} className="w-full mt-4 text-gray-500 hover:text-gray-300 text-sm">
            Back to Directory
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        {/* Remote video — 9:16 portrait, sized to dominate the viewport.
            Use viewport-height + viewport-width caps so the box scales up
            on big monitors instead of being clamped by the parent flex's
            available space. */}
        <div
          className={`relative ${status === 'connected' ? '' : 'hidden'}`}
          style={{
            aspectRatio: '9/16',
            height: 'min(95vh, calc(95vw * 16 / 9))',
          }}
        >
          <video ref={remoteVideoRef} autoPlay playsInline style={{ objectFit: 'contain', width: '100%', height: '100%' }} className="bg-black" />
        </div>

        {/* Calling state */}
        {status === 'calling' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="relative mb-4">
                <div className="relative w-24 h-24 mx-auto">
                  <div className="absolute inset-0 rounded-full bg-emerald-500/30 animate-ping" style={{animationDuration: '1.5s'}} />
                  <div className="absolute inset-2 rounded-full bg-emerald-500/20 animate-ping" style={{animationDuration: '1.5s', animationDelay: '0.2s'}} />
                  <div className="absolute inset-4 rounded-full bg-emerald-500/40 animate-ping" style={{animationDuration: '1.5s', animationDelay: '0.4s'}} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-5xl">📞</span>
                  </div>
                </div>
              </div>
              <p className="text-white text-lg">Ringing resident...</p>
              <p className="text-gray-500 text-sm mt-2">Unit {unit?.unit_number} • {property?.name}</p>
            </div>
          </div>
        )}

        {/* No answer */}
        {status === 'no-answer' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <p className="text-yellow-400 text-xl mb-2">No answer</p>
              <p className="text-gray-400 text-sm mb-6">The resident did not answer your call</p>
              <div className="flex gap-3 justify-center">
                <button onClick={handleTryAgain} className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2.5 rounded-lg">Try Again</button>
                <button onClick={goBack} className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg">Back to Directory</button>
              </div>
            </div>
          </div>
        )}

        {/* Call ended */}
        {status === 'ended' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <p className="text-white text-lg mb-2">Call ended</p>
              {duration > 0 && <p className="text-gray-500 text-sm">{formatTime(duration)}</p>}
              <button onClick={goBack} className="mt-6 bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2.5 rounded-lg">Back to Directory</button>
            </div>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <p className="text-red-400 text-lg mb-2">{errorMsg || 'Something went wrong'}</p>
              <div className="flex gap-3 justify-center mt-4">
                <button onClick={handleTryAgain} className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2.5 rounded-lg">Try Again</button>
                <button onClick={goBack} className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-2.5 rounded-lg">Go Back</button>
              </div>
            </div>
          </div>
        )}

        {/* Local video (picture-in-picture) — anchored to the bottom-right
            above the call controls; the top is occupied by mobile nav bars
            which were obscuring the PiP at the previous top-right anchor. */}
        <div
          className="absolute right-4 w-28 h-20 rounded-lg overflow-hidden border border-gray-600 bg-gray-900 shadow-lg"
          style={{ bottom: 'calc(env(safe-area-inset-bottom) + 7rem)' }}
        >
          <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-contain bg-black" />
        </div>

        {/* Duration */}
        {status === 'connected' && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm rounded-full px-4 py-1">
            <span className="text-white text-sm font-mono">{formatTime(duration)}</span>
          </div>
        )}

        {(status === 'calling' || status === 'connected' || status === 'error') && audioDiagnostics.length > 0 && (
          <details className="absolute left-3 bottom-3 z-20 max-w-[calc(100%-1.5rem)] rounded bg-black/80 p-2 text-[10px] text-gray-300">
            <summary className="cursor-pointer text-gray-200">Audio diagnostics ({audioDiagnostics.length})</summary>
            <pre className="mt-2 max-h-40 max-w-[85vw] overflow-auto whitespace-pre-wrap break-all">{audioDiagnostics.join('\n')}</pre>
          </details>
        )}
      </div>

      {/* Controls */}
      {(status === 'calling' || status === 'connected') && (
        <div className="bg-gray-950 px-6 pt-4 pb-8 flex justify-center gap-4" style={{paddingBottom: "max(2rem, env(safe-area-inset-bottom))"}}>
          <button onClick={toggleMute} className={`${muted ? 'bg-red-600' : 'bg-gray-700'} text-white rounded-full w-12 h-12 flex items-center justify-center`}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={muted ? 'M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2' : 'M15.536 8.464a5 5 0 010 7.072M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z'} />
            </svg>
          </button>
          <button onClick={toggleVideo} className={`${!videoEnabled ? 'bg-red-600' : 'bg-gray-700'} text-white rounded-full w-12 h-12 flex items-center justify-center`}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button onClick={handleHangup} className="bg-red-600 hover:bg-red-500 text-white rounded-full w-12 h-12 flex items-center justify-center">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
